import React, { useState } from 'react';
import {
    Box,
    Button,
    Typography,
    IconButton,
    ImageList,
    ImageListItem,
    ImageListItemBar,
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    Delete as DeleteIcon,
    PhotoCamera as CameraIcon,
} from '@mui/icons-material';
import { compressImage } from '../../utils/helpers';
import LoadingSpinner from '../shared/LoadingSpinner';
import ErrorMessage from '../shared/ErrorMessage';

const StepUploadDocuments = ({ images, setImages, onOCR, loading, error }) => {
    const [previews, setPreviews] = useState([]);

    const handleFileSelect = async (event) => {
        const files = Array.from(event.target.files);

        if (files.length + images.length > 3) {
            alert('Bạn chỉ có thể upload tối đa 3 ảnh');
            return;
        }

        try {
            const compressedImages = await Promise.all(
                files.map(async (file) => {
                    // Check file size (max 5MB)
                    if (file.size > 5 * 1024 * 1024) {
                        throw new Error(`File ${file.name} quá lớn (tối đa 5MB)`);
                    }

                    // Compress image
                    const base64 = await compressImage(file, 2000, 2000);
                    return {
                        base64,
                        name: file.name,
                        preview: base64,
                    };
                })
            );

            setImages([...images, ...compressedImages]);
            setPreviews([...previews, ...compressedImages.map(img => img.preview)]);
        } catch (err) {
            alert(err.message);
        }
    };

    const handleRemoveImage = (index) => {
        const newImages = images.filter((_, i) => i !== index);
        const newPreviews = previews.filter((_, i) => i !== index);
        setImages(newImages);
        setPreviews(newPreviews);
    };

    const handleOCR = () => {
        if (images.length === 0) {
            alert('Vui lòng upload ít nhất 1 ảnh');
            return;
        }
        onOCR();
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Upload Giấy Tờ Tùy Thân
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Upload ảnh thẻ nhân viên (bắt buộc). Ảnh CCCD hoặc ảnh thông tin từ app VNeID là không bắt buộc nhưng giúp tự động điền thông tin nhanh hơn.
            </Typography>

            {/* Upload Buttons */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadIcon />}
                    disabled={images.length >= 3 || loading}
                >
                    Chọn ảnh
                    <input
                        type="file"
                        hidden
                        accept="image/jpeg,image/png,image/jpg"
                        multiple
                        onChange={handleFileSelect}
                    />
                </Button>

                <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CameraIcon />}
                    disabled={images.length >= 3 || loading}
                >
                    Chụp ảnh
                    <input
                        type="file"
                        hidden
                        accept="image/*"
                        capture="environment"
                        onChange={handleFileSelect}
                    />
                </Button>
            </Box>

            {/* Image Previews */}
            {images.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <ImageList cols={3} gap={8} sx={{ mb: 2 }}>
                        {images.map((img, index) => (
                            <ImageListItem key={index}>
                                <img
                                    src={img.preview}
                                    alt={`Preview ${index + 1}`}
                                    loading="lazy"
                                    style={{
                                        height: '150px',
                                        objectFit: 'cover',
                                        borderRadius: '8px',
                                    }}
                                />
                                <ImageListItemBar
                                    position="top"
                                    actionIcon={
                                        <IconButton
                                            sx={{ color: 'white' }}
                                            onClick={() => handleRemoveImage(index)}
                                            disabled={loading}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    }
                                    sx={{
                                        background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
                                    }}
                                />
                            </ImageListItem>
                        ))}
                    </ImageList>

                    <Typography variant="body2" color="text.secondary">
                        Đã chọn {images.length}/3 ảnh
                    </Typography>
                </Box>
            )}

            <ErrorMessage error={error} />

            {loading && <LoadingSpinner message="Đang quét thông tin..." />}

            {/* OCR Button */}
            {!loading && (
                <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleOCR}
                    disabled={images.length === 0}
                >
                    Quét Thông Tin
                </Button>
            )}
        </Box>
    );
};

export default StepUploadDocuments;
